import React, {
    useContext, useState, useEffect, useRef, useCallback
} from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { HeaderContext } from '@contexts/HeaderContext';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import { LoginDataContext } from '@contexts/LoginDataContext';
import { LoginDataAction } from '@reducers/LoginDataReducer';
import {
    CheckLoginCodeErrorCode, CheckLoginCodeErrorMessage, CheckLoginCodeStrategy, LoginInputMaxLength,
    LoginErrorCode, LoginErrorMessage
} from '@utils/LoginProcess/constants';
import { checkBeforeLoginCode, getBeforeLoginCode, postLoginData } from '@utils/LoginProcess/requests';
import { getCookie, setCookie, delCookie } from '@utils/cookieUtils';
import { postLoginCallback, getBeforeLoginCodeCallback } from '@utils/LoginProcess/LoginProcess';
import { addBreadcrumb, setTag, captureException } from '@sentry/nextjs';
import Gesture from './Gesture';
import LoginErrorTooltip from './LoginErrorTooltip';
import debounce from '../../../utils/debounce';

const LoginModal = () => {
    const [state, dispatch] = useContext(HeaderContext);
    const { isShowLoginPopup } = state;
    const [loginData, loginDataDispatch] = useContext(LoginDataContext);
    const { t } = useTranslation();
    const [isWrongLoginCode, setIsWrongLoginCode] = useState(false);
    const [loginTooltip, setLoginTooltip] = useState([]);
    const [isShowPassword, setIsShowPassword] = useState(false);
    const [checkRememberMe, setCheckRememberMe] = useState(false);
    const [loginMode, setLoginMode] = useState('keypad');
    const [isDomLoaded, setIsDomLoaded] = useState(false);
    const { username } = loginData;
    const {
        openGesture, ip, clientApiUrl, rsaPublicKey: rsaInfo
    } = useSiteInfo();
    const { locale } = useRouter();
    const [isRequired, setIsRequired] = useState(false);
    const [isLoginCodeRefresh, setIsLoginCodeRefresh] = useState(false);
    const [isShowGestureTooltip, setIsShowGestureTooltip] = useState(false);
    const [userNameIndex, setUserNameIndex] = useState(0);
    const [isLoginBtnDisable, setIsLoginBtnDisable] = useState(false);
    const userNameInput = useRef(null);

    const getRememberMeBooleanValue = () => {
        // to handle getCookie('rememberMe') is null / 'null' / 'undefined' issue
        const rememberMe = getCookie('rememberMe') ? getCookie('rememberMe') : false;
        const rememberMeValue = ['undefined', 'null'].includes(rememberMe) ? false : JSON.parse(rememberMe);
        return rememberMeValue;
    };

    useEffect(() => {
        setIsDomLoaded(true);
        // 第一次渲染完成，初始化rememberMe state from Cookie('rememberMe')
        const rememberMeStatus = getRememberMeBooleanValue();
        setCheckRememberMe(rememberMeStatus);
    }, []);

    useEffect(() => {
        if (userNameInput && userNameInput.current) {
            userNameInput.current.focus();
        }
    }, [loginMode]);

    const initInputAlertState = () => {
        setLoginTooltip([]);
        setIsRequired(false);
        passwordValidator('password', loginData.password);
    };
    const passwordValidator = useCallback(
        debounce((id, value) => {
            if (id === 'password' && value.length > 10 ) {
                loginDataDispatch({
                    type: LoginDataAction.SHOWING_ERROR_MESSAGE,
                    payload: {
                        errorMessage: {
                            errorMessage: CheckLoginCodeErrorMessage.LimitPassword,
                        },
                    },
                });
                setIsLoginBtnDisable(true);
            } else {
                loginDataDispatch({ type: LoginDataAction.NOT_SHOWING_ERROR_MESSAGE });
                setIsLoginBtnDisable(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        return () => {
            passwordValidator.cancel();
        };
    }, [passwordValidator]);

    const handleChangeLoginInput = (e) => {
        const { id, value } = e.target;
        initInputAlertState();
        loginDataDispatch({
            type: LoginDataAction.UPDATE_LOGIN_DATA,
            payload: {
                key: id,
                value,
            },
        });
        passwordValidator(id, value);
    };

    const handleChangeLoginCode = (e) => {
        const { value } = e.target;
        loginDataDispatch({
            type: LoginDataAction.UPDATE_LOGIN_DATA,
            payload: {
                key: 'loginCode',
                value,
            },
        });
        setIsWrongLoginCode(false);
    };

    const refreshLoginCode = () => {
        setIsLoginCodeRefresh(true);

        setTimeout(() => {
            setIsLoginCodeRefresh(false);
        }, 1000);

        getBeforeLoginCode(clientApiUrl)
            .then((data) => (getBeforeLoginCodeCallback(data, loginData, loginDataDispatch)))
            .catch((error) => console.error(error));
    };

    const getGestureUsers = () => {
        const gesUser = getCookie('ges') || '';
        if (!gesUser) return [];
        const gestureUsers = gesUser.split(',');
        return gestureUsers;
    };

    const getGestureDropDownUsers = () => {
        const gesUser = getCookie('UserName') && !getCookie('UserNameList')
            ? getCookie('UserName') : getCookie('UserNameList');
        const gestureUsers = gesUser && gesUser.split(',');

        return gestureUsers;
    };

    const checkGesture = () => {
        if (!openGesture) {
            loginDataDispatch({ type: LoginDataAction.NOT_SHOWING_GESTURE_LOGIN_FORM });
            return;
        }

        const isMobilePlatform = state.platform === 'mobile';
        const hasRememberMeChecked = checkRememberMe;
        const isUserInGestureList = getGestureUsers().includes(username?.toLowerCase());

        if (isUserInGestureList && hasRememberMeChecked && isMobilePlatform) {
            loginDataDispatch({ type: LoginDataAction.SHOWING_GESTURE_LOGIN_FORM });
        } else {
            setLoginMode('keypad');
            loginDataDispatch({ type: LoginDataAction.NOT_SHOWING_GESTURE_LOGIN_FORM });
        }
    };

    const changeGestureUser = (value) => {
        const getUserListIndex = getGestureDropDownUsers()?.findIndex((item) => item === value);
        initInputAlertState();
        setUserNameIndex(getUserListIndex);
        loginDataDispatch({
            type: LoginDataAction.UPDATE_LOGIN_DATA,
            payload: {
                key: 'username',
                value,
            },
        });
    };

    const addGestureAccount = () => {
        setLoginMode('keypad');
    };

    const checkLoginCodeStrategies = {
        [CheckLoginCodeStrategy.MakeSuccess]: () => {
            loginDataDispatch({ type: LoginDataAction.NOT_SHOWING_LOGIN_CODE_IMAGE });
            loginDataDispatch({ type: LoginDataAction.NOT_SHOWING_ERROR_MESSAGE });
            checkGesture();
        },
        [CheckLoginCodeStrategy.RefreshLoginCode]: () => {
            setIsWrongLoginCode(true);
            loginDataDispatch({
                type: LoginDataAction.SHOWING_ERROR_MESSAGE,
                payload: {
                    errorMessage: {
                        errorMessage: CheckLoginCodeErrorMessage.InvalidLoginCode,
                    },
                },
            });
            refreshLoginCode();
        },
    };

    const checkBeforeLoginCodeCallback = (response) => {
        loginDataDispatch({
            type: LoginDataAction.UPDATE_LOGIN_DATA,
            payload: {
                key: 'loginCode',
                value: '',
            },
        });
        const rule = {
            [CheckLoginCodeErrorCode.Success]: CheckLoginCodeStrategy.MakeSuccess,
            [CheckLoginCodeErrorCode.CheckLoginCodeFail]: CheckLoginCodeStrategy.RefreshLoginCode,
        }[response.errorCode];

        checkLoginCodeStrategies[rule](response);
    };

    const handleCheckLoginCode = () => {
        if (loginData.loginCode === '' && loginData.isShowLoginCodeForm) {
            setIsWrongLoginCode(true);
            loginDataDispatch({
                type: LoginDataAction.SHOWING_ERROR_MESSAGE,
                payload: {
                    errorMessage: {
                        errorMessage: CheckLoginCodeErrorMessage.EmptyLoginCode,
                    },
                },
            });
            return;
        }

        checkBeforeLoginCode(clientApiUrl, loginData)
            .then((data) => (checkBeforeLoginCodeCallback(data)))
            .catch((error) => console.error(error));
    };

    const handleLogin = () => {
        if (isLoginBtnDisable) return;

        if (loginData.username === '') {
            setLoginTooltip(['emptyUsername']);
            return;
        }

        if (loginData.password === '') {
            setLoginTooltip(['emptyPassword']);
            return;
        }

        if (loginData.password !== '' && loginData.username !== '') {
            addBreadcrumb({
                category: 'login',
                message: 'loginData payload',
                data: {
                    loginData,
                    ip,
                    locale,
                },
            });

            postLoginData(clientApiUrl + '/api/Login', loginData, rsaInfo)
                .then((data) => {
                    setIsLoginBtnDisable(true);
                    postLoginCallback(
                        clientApiUrl, 
                        data, 
                        loginData, 
                        loginDataDispatch, 
                        locale, 
                        ip, 
                        checkRememberMe, 
                        false, // isGestureLogin
                        t, // t
                        null, // scb
                        null, // fcb
                        getGestureUsers, 
                        setLoginMode,
                    );
                    if ([LoginErrorCode.LoginTooOften, LoginErrorCode.LoginTooOftenPleaseWait5Minutes].includes(data.errorCode)) {
                        const error = new Error(`Login Failed - Error Code: ${data.errorCode}`);
                        error.status = data.errorCode;
                        error.response = data;
                        const tagMap = {
                            [LoginErrorCode.LoginTooOften]: LoginErrorMessage.LoginTooOften,
                            [LoginErrorCode.LoginTooOftenPleaseWait5Minutes]: LoginErrorMessage.LoginTooOftenPleaseWait5Minutes,
                        };
                        setTag("Login", tagMap[data.errorCode]);
                        captureException(error, {
                            level: 'error',
                            extra: {
                                errorCode: data.errorCode,
                                response: data,
                            },
                        });
                    }
                    if (data.errorCode !== LoginErrorCode.Success) {
                        setTimeout(() => {
                            setIsLoginBtnDisable(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setTag("Login", "api_failed");
                    captureException(error, {
                        level: 'error',
                        extra: {
                            errorContent: error,
                            clientApiUrl,
                            apiName: '/api/Login',
                            loginData,
                        },
                    });
                });
        }
    };

    const changeRememberUser = (e) => {
        e.preventDefault();
        setCheckRememberMe(!checkRememberMe);
    };

    const closeLoginPopup = () => {
        dispatch({ type: 'SHOW_LOGIN_POPUP' });
        loginDataDispatch({
            type: LoginDataAction.NOT_SHOWING_ERROR_MESSAGE,
        });
        initInputAlertState();
    };

    const handleShowGestureTooltip = () => {
        const isGestureTooltipShown = getCookie('IS_GESTURE_TOOLTIP_SHOWN');

        if (isGestureTooltipShown && loginMode === 'gesture-password') {
            setIsShowGestureTooltip(false);
            return;
        }

        if (!isGestureTooltipShown && loginMode === 'keypad') {
            setIsShowGestureTooltip(true);

            setCookie('IS_GESTURE_TOOLTIP_SHOWN', true, 365);
            setTimeout(() => {
                setIsShowGestureTooltip(false);
            }, 5000);
        }
    };

    const checkDuplicate = (array1, array2) => {
        return array2?.some((item) => array1.includes(item));
    };

    useEffect(() => {
        checkGesture();
    }, [username, checkRememberMe, loginMode, state.platform]);

    useEffect(() => {
        const rememberMeStatus = getRememberMeBooleanValue();
        if (!rememberMeStatus) return;
        const usernameInCookie = getCookie('UserName');

        loginDataDispatch({
            type: LoginDataAction.UPDATE_LOGIN_DATA,
            payload: {
                key: 'username',
                value: usernameInCookie?.split(',')[0] || '',
            },
        });

        const gestureUsers = getGestureUsers();
        const isDuplicate = checkDuplicate(gestureUsers, usernameInCookie?.split(','));

        if (openGesture && gestureUsers.length > 0 && isDuplicate && state.platform === 'mobile') {
            setLoginMode('gesture-password');
        }

        if (state.platform === 'desktop') {
            setLoginMode('keypad');
        }
    }, [loginDataDispatch, openGesture, state.platform]);

    useEffect(()=>{
        delCookie('IsBDLogin')
    },[])

    useEffect(() => {
        if (loginMode === 'gesture-password' && isShowLoginPopup) {
            const isGestureTooltipShown = getCookie('IS_GESTURE_TOOLTIP_SHOWN');

            if (!isGestureTooltipShown) {
                setIsShowGestureTooltip(true);
                setCookie('IS_GESTURE_TOOLTIP_SHOWN', true, 365);
                setTimeout(() => {
                    setIsShowGestureTooltip(false);
                }, 5000);
            }
        }
    }, [isShowLoginPopup, loginMode]);

    if (!isDomLoaded) return [];

    return (
        <div className="modal modal--login" data-open={isShowLoginPopup} onMouseDown={closeLoginPopup}>
            <div className="modal__dialog" onMouseDown={(e) => e.stopPropagation()}>
                <div className="modal__close" onClick={closeLoginPopup}>
                    <a className="btn btn--icon">
                        <i className="icon icon--close" />
                    </a>
                </div>
                <div className="tooltip tooltip--top-right" data-open={isShowGestureTooltip}>
                    <div className="text">{t('lbl_UsePwdTooltip')}</div>
                </div>
                <div className="modal__header">
                    <span className="text">
                        {loginData.isShowLoginCodeForm
                            ? t('lbl_EnterValidCode')
                            : t('lbl_login')}
                    </span>
                    {loginData.gestureToggle && (
                        <div
                            className="login-switch"
                            data-mode={loginMode}
                            onClick={() => {
                                setLoginMode(loginMode === 'keypad' ? 'gesture-password' : 'keypad');
                                handleShowGestureTooltip();
                            }}
                        >
                            <a className="btn btn--icon">
                                <i className="icon icon--gesture-password" />
                                <i className="icon icon--keypad" />
                            </a>
                        </div>
                    )}
                </div>

                <div
                    className="login-form"
                    data-open={
                        !loginData.isShowLoginCodeForm
                        && loginMode === 'keypad'
                    }
                >
                    <div className="login-form__item">
                        <LoginErrorTooltip
                            key="usernameError"
                            errorType="usernameError"
                            tooltip={loginTooltip}
                            setIsRequired={setIsRequired}
                        />
                        <div className="textfield">
                            <div className="textfield__icon">
                                <i className="icon icon--my-account" />
                            </div>
                            <input
                                className="textfield__input"
                                type="text"
                                placeholder={t('lbl_UName')}
                                value={loginData.username}
                                id="username"
                                maxLength={LoginInputMaxLength.usernameMaxLength}
                                onChange={handleChangeLoginInput}
                                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                                required={isRequired}
                                ref={userNameInput}
                            />
                            <span className="textfield__bg" />
                        </div>
                    </div>
                    <div className="login-form__item">
                        <LoginErrorTooltip
                            key="passwordError"
                            errorType="passwordError"
                            tooltip={loginTooltip}
                            setIsRequired={setIsRequired}
                        />
                        <div className="textfield">
                            <div className="textfield__icon">
                                <i className="icon icon--lock" />
                            </div>
                            <input
                                className="textfield__input"
                                type={isShowPassword ? 'text' : 'password'}
                                placeholder={t('lbl_login_password')}
                                value={loginData.password}
                                id="password"
                                maxLength={LoginInputMaxLength.passwordMaxLength}
                                onChange={handleChangeLoginInput}
                                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                                required={isRequired}
                            />
                            <a
                                className="btn btn--icon"
                                data-selected={isShowPassword}
                                onClick={() => setIsShowPassword(!isShowPassword)}
                            >
                                <i className="icon icon--visibility-off" />
                                <i className="icon icon--visibility" />
                            </a>
                            <span className="textfield__bg" />
                        </div>
                    </div>
                    <div className="login-form__item">
                        <label
                            aria-checked={checkRememberMe}
                            className="checkbox"
                            htmlFor={checkRememberMe.toString()}
                            onClick={(e) => changeRememberUser(e)}
                            tabIndex=""
                            role="checkbox"
                        >
                            <input
                                type="checkbox"
                                value={checkRememberMe}
                                checked={checkRememberMe}
                                onChange={() => {}}
                            />
                            <i className="icon icon--checkbox" />
                            <span className="text">{t('lbl_RememberMe')}</span>
                        </label>
                    </div>
                    <div
                        className="login-form__item login-form__item--failed"
                        data-open={loginData.isShowLoginErrorMessage}
                    >
                        <span className="text-void">
                            {(loginData?.errorMessage?.replacedText)
                                ? (t(
                                    loginData?.errorMessage?.errorMessage,
                                    {
                                        0: loginData?.errorMessage?.replacedText,
                                        1: loginData?.errorMessage?.replacedText1,
                                    },
                                ))
                                : t([loginData?.errorMessage?.errorMessage])}
                        </span>
                    </div>
                    <div className="login-form__item" data-disable={isLoginBtnDisable} onClick={handleLogin}>
                        <a className="btn btn--secondary">
                            <span className="text">{t('lbl_login')}</span>
                        </a>
                    </div>
                </div>

                <div className="login-form login-form--verified" data-open={loginData.isShowLoginCodeForm}>
                    <div className="login-form__item">
                        <span className="text">{t('lbl_InputValidCode')}</span>
                    </div>
                    <div className="login-form__item">
                        <div className="textfield">
                            <div className="textfield__icon">
                                <i className="icon icon--shield" />
                            </div>
                            <input
                                className="textfield__input"
                                type="text"
                                placeholder={t('lbl_Captcha')}
                                value={loginData.loginCode}
                                id="loginCode"
                                maxLength={LoginInputMaxLength.loginCodeMaxLength}
                                onChange={handleChangeLoginCode}
                                onKeyDown={(e) => e.key === 'Enter' && handleCheckLoginCode()}
                                required={isWrongLoginCode}
                            />
                            <div className="img">
                                <img src={`data:image/png;base64,${loginData.loginCodeImage}`} alt="" />
                            </div>
                            <a
                                className="btn btn--refresh"
                                data-refresh={isLoginCodeRefresh}
                                onClick={refreshLoginCode}
                            >
                                <i className="icon icon--refresh" />
                            </a>
                            <span className="textfield__bg" />
                        </div>
                        <div className="tooltip tooltip--bottom" data-open={isWrongLoginCode}>
                            <span className="text">
                                {(loginData?.errorMessage?.replacedText)
                                    ? t(
                                        loginData?.errorMessage?.errorMessage,
                                        { 0: loginData?.errorMessage?.replacedText },
                                    )
                                    : t([loginData?.errorMessage?.errorMessage])}
                            </span>
                        </div>
                    </div>
                    <div className="login-form__item">
                        <a className="btn btn--secondary" onClick={handleCheckLoginCode}>
                            <span className="text">{t('lbl_Submit')}</span>
                        </a>
                    </div>
                </div>
                <Gesture
                    loginMode={loginMode}
                    username={username}
                    setLoginMode={setLoginMode}
                    getGestureUsers={getGestureUsers}
                    // getGestureDropDownUsers={getGestureDropDownUsers}
                    changeGestureUser={changeGestureUser}
                    addGestureAccount={addGestureAccount}
                    userNameIndex={userNameIndex}
                />
            </div>
        </div>
    );
};

export default LoginModal;
